/* ---------------------------------------------------------------------------
   THE ROW MODEL.

   The aggregated diff arrives as nested structure — repos, then files, then
   hunks, then lines — and a reviewer scrolls it as one continuous surface. A
   virtualiser cannot window a tree, so this module flattens the whole selection
   into ONE array of rows and computes a prefix-sum offset table over it.

   Heights are per-kind and exact, not estimated. That matters: a virtualiser
   that guesses row heights drifts, and a drifting diff puts a `-` line under the
   wrong file header — which on this screen is not a cosmetic bug, it is a
   reviewer approving a change they did not read. Line rows are the fixed 18px
   `.diff-line` from index.css and never wrap; a long line scrolls horizontally
   with the pane.

   Nothing here diffs anything. The hunks are computed server-side (contract:
   GET /expert/chats/{chatId}/diff) precisely so the browser never parses patch
   text, and so line numbers arrive pre-resolved per side and any window can be
   rendered without walking the hunk from the top.
--------------------------------------------------------------------------- */
import type { AggregatedDiff, ChangeKind, DiffFile, DiffLine, RepoDiff } from '@/types/api';

export const ROW_HEIGHT = {
  repo: 34,
  file: 38,
  hunk: 24,
  line: 18,
  note: 32,
} as const;

export type DiffRow =
  | { kind: 'repo'; key: string; repo: RepoDiff }
  | { kind: 'file'; key: string; repoId: number; repoSlug: string; file: DiffFile }
  | { kind: 'hunk'; key: string; header: string; oldStart: number; newStart: number }
  | {
      kind: 'line';
      key: string;
      line: DiffLine;
      language: string | null;
      /** Path, for the copy-a-line-reference affordance. */
      path: string;
    }
  | { kind: 'note'; key: string; tone: 'neutral' | 'warn'; text: string };

export interface FileEntry {
  key: string;
  repoId: number;
  repoSlug: string;
  path: string;
  changeKind: ChangeKind;
  additions: number;
  deletions: number;
  isBinary: boolean;
  truncated: boolean;
}

export interface RepoGroup {
  repoId: number;
  slug: string;
  name: string;
  position: number;
  changed: boolean;
  truncated: boolean;
  additions: number;
  deletions: number;
  baseRef: string | null;
  headRef: string | null;
  baseSha: string | null;
  headSha: string | null;
  files: FileEntry[];
}

export function fileKey(repoId: number, path: string): string {
  return `${repoId}:${path}`;
}

/** Per-repo grouping, in promotion order — the order the merges will actually
    run in, which is the only order a reviewer should read a promotion diff in. */
export function groupRepos(diff: AggregatedDiff | undefined): RepoGroup[] {
  if (!diff) return [];
  return [...diff.repos]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.repo_id - b.repo_id)
    .map((repo) => ({
      repoId: repo.repo_id,
      slug: repo.repo_slug,
      name: repo.repo_name ?? repo.repo_slug,
      position: repo.position ?? 0,
      changed: repo.changed,
      truncated: repo.truncated ?? false,
      additions: repo.additions ?? 0,
      deletions: repo.deletions ?? 0,
      baseRef: repo.base_ref ?? null,
      headRef: repo.head_ref ?? null,
      baseSha: repo.base_sha ?? null,
      headSha: repo.head_sha ?? null,
      files: repo.files.map((file) => ({
        key: fileKey(repo.repo_id, file.path),
        repoId: repo.repo_id,
        repoSlug: repo.repo_slug,
        path: file.path,
        changeKind: file.change_kind,
        additions: file.additions,
        deletions: file.deletions,
        isBinary: file.is_binary,
        truncated: file.truncated,
      })),
    }));
}

export interface BuildOptions {
  /** Substring filter over paths. Empty means everything. */
  filter: string;
  /** Repos the reviewer has collapsed. Their files are not built at all. */
  collapsedRepos: ReadonlySet<number>;
  /** When set, only this file is built — the file-tree click target. */
  onlyFile: string | null;
}

export interface RowModel {
  rows: DiffRow[];
  /** offsets[i] is the y of row i; offsets[rows.length] is the total height. */
  offsets: number[];
  totalHeight: number;
  /** Row index of each file's header, so the tree can scroll to it. */
  fileAnchors: Map<string, number>;
  /** How many files the filter hid. Shown, so a filter never silently lies. */
  hiddenFileCount: number;
}

function matches(path: string, needle: string): boolean {
  if (!needle) return true;
  return path.toLowerCase().includes(needle);
}

/**
 * Flatten the selection into rows.
 *
 * Unchanged repos are kept as a single note row rather than dropped, because the
 * contract returns them deliberately: an expert must be able to see that a repo
 * was considered and had nothing in it, which is a different fact from a repo
 * that was not looked at.
 */
export function buildRows(diff: AggregatedDiff | undefined, options: BuildOptions): RowModel {
  const rows: DiffRow[] = [];
  const fileAnchors = new Map<string, number>();
  let hiddenFileCount = 0;

  if (!diff) return { rows, offsets: [0], totalHeight: 0, fileAnchors, hiddenFileCount };

  const needle = options.filter.trim().toLowerCase();
  const repos = [...diff.repos].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.repo_id - b.repo_id,
  );

  for (const repo of repos) {
    const matched = repo.files.filter((file) => matches(file.path, needle));
    // Only the FILTER counts as hiding something. Isolating one file is an
    // explicit choice the reviewer made and reporting it as "9 files hidden"
    // would read as a warning about their own click.
    hiddenFileCount += repo.files.length - matched.length;

    const visible = options.onlyFile
      ? matched.filter((file) => fileKey(repo.repo_id, file.path) === options.onlyFile)
      : matched;

    // A repo with nothing to show under the current filter is not drawn at all —
    // except an unchanged repo under no filter, which is drawn to say so.
    const filtering = Boolean(needle) || options.onlyFile !== null;
    if (visible.length === 0 && filtering) continue;

    rows.push({ kind: 'repo', key: `repo:${repo.repo_id}`, repo });

    if (options.collapsedRepos.has(repo.repo_id)) continue;

    if (!repo.changed) {
      rows.push({
        kind: 'note',
        key: `repo:${repo.repo_id}:unchanged`,
        tone: 'neutral',
        text: 'No changes. This repo was diffed against the target and is already up to date.',
      });
      continue;
    }

    if (repo.truncated) {
      rows.push({
        kind: 'note',
        key: `repo:${repo.repo_id}:truncated`,
        tone: 'warn',
        text: 'This repo exceeded the aggregate line budget. Open it on its own to lift the budget and read the full diff.',
      });
    }

    for (const file of visible) {
      const key = fileKey(repo.repo_id, file.path);
      fileAnchors.set(key, rows.length);
      rows.push({ kind: 'file', key: `file:${key}`, repoId: repo.repo_id, repoSlug: repo.repo_slug, file });

      if (file.is_binary) {
        rows.push({
          kind: 'note',
          key: `file:${key}:binary`,
          tone: 'neutral',
          text: 'Binary file. Counts are recorded; there are no hunks to read.',
        });
        continue;
      }

      if (file.hunks.length === 0) {
        rows.push({
          kind: 'note',
          key: `file:${key}:empty`,
          tone: 'neutral',
          text: file.truncated
            ? 'Over the per-file line budget. The counts above are accurate; the hunks were cut.'
            : 'No line changes — a mode or metadata change only.',
        });
        continue;
      }

      for (const [hunkIndex, hunk] of file.hunks.entries()) {
        rows.push({
          kind: 'hunk',
          key: `hunk:${key}:${hunkIndex}`,
          header: hunk.header ?? '',
          oldStart: hunk.old_start,
          newStart: hunk.new_start,
        });
        for (const [lineIndex, line] of hunk.lines.entries()) {
          rows.push({
            kind: 'line',
            key: `l:${key}:${hunkIndex}:${lineIndex}`,
            line,
            language: file.language ?? null,
            path: file.path,
          });
        }
      }

      if (file.truncated) {
        rows.push({
          kind: 'note',
          key: `file:${key}:cut`,
          tone: 'warn',
          text: 'Truncated at the per-file line budget. Bounded, never silently dropped — the counts above are the real ones.',
        });
      }
    }
  }

  const offsets = new Array<number>(rows.length + 1);
  let y = 0;
  for (let i = 0; i < rows.length; i += 1) {
    offsets[i] = y;
    y += ROW_HEIGHT[rows[i]!.kind];
  }
  offsets[rows.length] = y;

  return { rows, offsets, totalHeight: y, fileAnchors, hiddenFileCount };
}

/** First row index whose offset+height is past `y`. Binary search — a linear
    scan is fine at 500 rows and quadratic at 200,000. */
export function rowIndexAt(offsets: readonly number[], y: number): number {
  let lo = 0;
  let hi = offsets.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((offsets[mid + 1] ?? Number.MAX_SAFE_INTEGER) <= y) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}
